(function () {

  // Guard: skip registration if the element is already defined
  if (customElements.get('variant-pills-block')) return;

  customElements.define('variant-pills-block', class extends HTMLElement {

    // ─── Lifecycle ──────────────────────────────────────────────────────────

    connectedCallback() {
      /**
       * Wait for the DOM to be ready before initialising.
       * Pills may be injected after the element itself is connected
       * (e.g. by Liquid section rendering), so we defer two rAF ticks
       * if no pills exist yet.
       */
      if (this.querySelector('.vp-pill')) {
        this._init();
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this._init());
        });
      }
    }

    // ─── Initialisation ─────────────────────────────────────────────────────

    _init() {
      // Cache references to key elements
      this._card      = this.closest('product-card');          // Parent card element
      this._pills     = this.querySelectorAll('.vp-pill');     // All variant pills
      this._cta       = this.querySelector('.vp-hover-cta');   // CTA wrapper shown on hover
      this._btnAdd    = this.querySelector('.vp-cta--add');    // "Add to cart" button
      this._variantId = null;                                   // Currently selected variant ID
      this._warningTimeout = null;                              // Bottom warning banner auto-hide timer

      // Nothing to do if there are no pills
      if (!this._pills.length) return;

      // Position the CTA overlay relative to the product image
      requestAnimationFrame(() => this._positionCta());
      window.addEventListener('resize', () => this._positionCta(), { passive: true });

      // Pill click: select variant and show CTA
      this._pills.forEach(pill => {
        pill.addEventListener('click', e => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this._selectPill(pill);
        });
      });

      // "Add to cart" click: POST to cart API
      this._btnAdd?.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (this._variantId) this._addToCart(this._variantId);
      });

      /**
       * When the user hovers a different card, reset this one.
       * Prevents stale selections persisting across cards.
       */
      this._card?.addEventListener('mouseenter', () => {
        document.querySelectorAll('variant-pills-block').forEach(block => {
          if (block !== this) block._reset();
        });
      });
    }

    // ─── Pill Selection ─────────────────────────────────────────────────────

    /**
     * Mark a pill as selected and store its variant ID.
     * Adds `.vp-variant-chosen` to the CTA so CSS can reveal the button.
     */
    _selectPill(pill) {
      this._pills.forEach(p => p.classList.remove('is-selected'));
      pill.classList.add('is-selected');
      this._variantId = pill.dataset.variantId;
      this._cta?.classList.add('vp-variant-chosen');
    }

    /**
     * Clear selection state — called when focus moves to another card.
     */
    _reset() {
      this._variantId = null;
      this._pills.forEach(p => p.classList.remove('is-selected'));
      this._cta?.classList.remove('vp-variant-chosen');
    }

    // ─── CTA Positioning ────────────────────────────────────────────────────

    /**
     * Align the CTA overlay to the bottom of the product image,
     * using a CSS custom property `--vp-cta-bottom` so the CTA
     * floats just above the card's content area.
     *
     * Layout assumption:
     *   .product-card__content > [0] = media/image element
     *   All other children = text/price content
     */
    _positionCta() {
      if (!this._card) return;

      const content = this._card.querySelector('.product-card__content');
      if (!content) return;

      const mediaEl = content.firstElementChild;
      if (!mediaEl) return;

      const cardRect  = this._card.getBoundingClientRect();
      const mediaRect = mediaEl.getBoundingClientRect();

      // Distance from the bottom of the image to the bottom of the card + 8px gap
      const bottomOffset = cardRect.bottom - mediaRect.bottom + 8;
      this._card.style.setProperty('--vp-cta-bottom', bottomOffset + 'px');
    }

    // ─── Dispatch Badge Conflict Check ──────────────────────────────────────

    /**
     * Checks whether adding the given dispatch badge value would conflict with
     * items already in the cart (Pre-Order items cannot mix with other products).
     * @param {string} newBadge - The dispatch badge value of the item being added.
     * @returns {Promise<{conflict: boolean, message?: string}>}
     */
    async _checkDispatchBadgeConflict(newBadge) {
      const cart = await fetch('/cart.js').then(r => r.json());
      const isNewPreOrder = newBadge === 'Pre-Order';

      const hasPreOrderInCart = cart.items.some(
        item => item.properties?._dispatch_badge === 'Pre-Order'
      );
      const hasAnyItemsInCart = cart.items.length > 0;

      if (isNewPreOrder && hasAnyItemsInCart && !hasPreOrderInCart) {
        return {
          conflict: true,
          message: 'Please remove other products from your cart before adding a Pre-Order item.'
        };
      }

      if (!isNewPreOrder && hasPreOrderInCart) {
        return {
          conflict: true,
          message: 'Please remove the Pre-Order product from your cart before adding other items.'
        };
      }

      return { conflict: false };
    }

    /**
     * Shows a warning banner fixed to the top of the page.
     * Reuses a single shared banner element across all product cards.
     * @param {string} message
     */
    _showBottomWarning(message) {
      let banner = document.querySelector('.vp-cart-warning-banner');
      if (!banner) {
        banner = document.createElement('div');
        banner.className = 'vp-cart-warning-banner';
        document.body.appendChild(banner);
      }
      banner.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="12" cy="12" r="10" fill="#e53e3e"/>
          <rect x="11" y="6" width="2" height="8" rx="1" fill="#fff"/>
          <rect x="11" y="16" width="2" height="2" rx="1" fill="#fff"/>
        </svg>
        <span>${message}</span>
      `;
      banner.classList.add('is-visible');

      clearTimeout(this._warningTimeout);
      this._warningTimeout = setTimeout(() => {
        banner.classList.remove('is-visible');
      }, 4000);
    }

    // ─── Cart Integration ───────────────────────────────────────────────────

    /**
     * Add the selected variant to the cart.
     *
     * Flow:
     *  0. Check dispatch badge conflict — block + warn if mismatched
     *  1. POST /cart/add.js       → add the item
     *  2. GET  /cart.js           → get updated item count
     *  3. GET  ?sections=…        → fetch rendered drawer HTML
     *  4. Swap cart-drawer-component in the DOM
     *  5. Update all cart bubble counters
     *  6. Dispatch cart:update    → Horizon re-renders drawer
     */
    async _addToCart(variantId) {
      const btn      = this._btnAdd;
      const origHTML = btn.innerHTML;
      const newBadge = this.dataset.dispatchBadge || '';

      // ── Step 0: Dispatch badge conflict check ────────────────────────
      const conflictResult = await this._checkDispatchBadgeConflict(newBadge);
      if (conflictResult.conflict) {
        this._showBottomWarning(conflictResult.message);
        return;
      }

      // Disable button while request is in flight
      btn.textContent = 'Adding...';
      btn.disabled    = true;

      try {

        // ── Step 1: Add to cart ─────────────────────────────────────────
        const addRes = await fetch('/cart/add.js', {
          method:  'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept':       'application/json'
          },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: 1,
            properties: { _dispatch_badge: newBadge }
          })
        });

        if (!addRes.ok) {
          const err = await addRes.json();
          throw new Error(err.description || 'Failed to add item');
        }

        // ── Step 2: Fetch updated cart state ────────────────────────────
        const cart  = await fetch('/cart.js').then(r => r.json());
        const count = cart.item_count;

        // ── Step 3: Fetch rendered section HTML ─────────────────────────
        const sectionsData = await fetch(
          `${window.location.pathname}?sections=cart-drawer-section`
        ).then(r => r.json());

        // ── Step 4: Swap cart drawer in the DOM ─────────────────────────
        const html       = sectionsData['cart-drawer-section'];
        const parser     = new DOMParser();
        const parsedDoc  = parser.parseFromString(html, 'text/html');
        const newDrawer  = parsedDoc.querySelector('cart-drawer-component');
        const oldDrawer  = document.querySelector('cart-drawer-component');

        if (newDrawer && oldDrawer) oldDrawer.replaceWith(newDrawer);

        // ── Step 5: Update cart bubble counters ─────────────────────────
        document.querySelectorAll('.cart-bubble__text-count').forEach(el => {
          el.textContent = count;
          el.classList.remove('hidden');
          el.removeAttribute('aria-hidden');

          // Ensure the parent bubble wrapper is visible
          el.closest('.cart-bubble')?.classList.remove('visually-hidden');
        });

        // ── Step 6: Dispatch Horizon cart:update event ──────────────────
        // Horizon listens for this event to open/refresh the drawer.
        // `source: 'product-form-component'` tells the drawer the origin.
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'variant-pills-block',
            data: {
              sections:  sectionsData,
              itemCount: count,
              source:    'product-form-component'
            }
          }
        }));

        // ── Success feedback ─────────────────────────────────────────────
        btn.textContent = 'Added ✓';

        setTimeout(() => {
          btn.innerHTML = origHTML;
          btn.disabled    = false;
          this._reset();
        }, 2000);

      } catch (err) {
        // ── Error feedback ───────────────────────────────────────────────
        console.error('[variant-pills-block] add to cart failed:', err);

        btn.textContent = 'Error — retry';
        btn.disabled    = false;

        setTimeout(() => {
          btn.innerHTML = origHTML;
        }, 2500);
      }
    }

  });

})();